// Bloques del recorrido: estado, banda superior, aviso, plan, pasos y campo de formulario.
import React from 'react';
import { Box, InputBase, InputBaseProps } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { J, TONE, JourneyTone } from '../journeyTokens';
import { JourneyCard, JourneyChip, JourneyText } from './JourneyBasics';

// --- Bloque de estado: icono, título, texto y acciones ---

export type StatusVariant = 'loading' | 'info' | 'success' | 'warning' | 'error' | 'blocked';

const STATUS: Record<Exclude<StatusVariant, 'loading'>, { tone: JourneyTone; icon: React.ReactNode }> = {
  info: { tone: 'info', icon: <InfoOutlinedIcon /> },
  success: { tone: 'ok', icon: <CheckCircleOutlineIcon /> },
  warning: { tone: 'warn', icon: <WarningAmberIcon /> },
  error: { tone: 'danger', icon: <ErrorOutlineIcon /> },
  blocked: { tone: 'warn', icon: <LockOutlinedIcon /> },
};

export const Spinner: React.FC = () => (
  <Box role="img" aria-label="Cargando" sx={{ width: 56, height: 56, borderRadius: '50%', border: `3px solid ${J.border}`, borderTopColor: J.sky, animation: 'journeySpin 0.9s linear infinite', '@keyframes journeySpin': { to: { transform: 'rotate(360deg)' } } }} />
);

export const StateIcon: React.FC<{ tone: JourneyTone; children: React.ReactNode }> = ({ tone, children }) => (
  <Box sx={{ width: 64, height: 64, borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: TONE[tone].bg.replace('.08', '.1'), border: `1px solid ${TONE[tone].border}`, color: TONE[tone].accent, '& svg': { fontSize: 30 } }}>
    {children}
  </Box>
);

export const StatusBlock: React.FC<{
  variant: StatusVariant;
  title: React.ReactNode;
  text?: React.ReactNode;
  chip?: React.ReactNode;
  actions?: React.ReactNode;
  footnote?: React.ReactNode;
  centered?: boolean;
  minHeight?: number;
}> = ({ variant, title, text, chip, actions, footnote, centered = variant === 'loading', minHeight }) => (
  <JourneyCard sx={{ display: 'flex', flexDirection: 'column', gap: '20px', minHeight, ...(centered ? { alignItems: 'center', justifyContent: 'center', textAlign: 'center' } : {}) }}>
    {chip}
    {variant === 'loading' ? <Spinner /> : <StateIcon tone={STATUS[variant].tone}>{STATUS[variant].icon}</StateIcon>}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: centered ? 'center' : undefined }}>
      <JourneyText variant="h2">{title}</JourneyText>
      {text && <JourneyText variant="body" sx={{ maxWidth: centered ? 360 : undefined }}>{text}</JourneyText>}
    </Box>
    {actions && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: centered ? 'center' : undefined }}>{actions}</Box>}
    {footnote && <JourneyText variant="small">{footnote}</JourneyText>}
  </JourneyCard>
);

// --- Banda superior: informativa, ámbar y roja ---

export type BannerTone = 'info' | 'amber' | 'red';

const BANNER: Record<BannerTone, { bg: string; border: string; color: string }> = {
  info: { bg: 'rgba(56,189,248,.07)', border: 'rgba(56,189,248,.25)', color: '#e0f2fe' },
  amber: { bg: 'rgba(252,211,77,.09)', border: 'rgba(252,211,77,.35)', color: '#fef3c7' },
  red: { bg: 'rgba(248,113,113,.09)', border: 'rgba(248,113,113,.35)', color: '#fee2e2' },
};

export const Meter: React.FC<{ value: number; tone?: 'sky' | 'amber' }> = ({ value, tone = 'sky' }) => (
  <Box component="span" sx={{ display: 'inline-block', width: 160, height: 6, borderRadius: 999, bgcolor: J.border, overflow: 'hidden', verticalAlign: 'middle' }}>
    <Box component="span" sx={{ display: 'block', height: '100%', width: `${Math.max(0, Math.min(100, value))}%`, bgcolor: tone === 'amber' ? J.amber : J.sky, borderRadius: 999 }} />
  </Box>
);

export const TopBanner: React.FC<{
  tone: BannerTone;
  title: React.ReactNode;
  details?: React.ReactNode[];
  action?: React.ReactNode;
  rounded?: boolean;
}> = ({ tone, title, details = [], action, rounded }) => (
  <Box role="status" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px 24px', px: { xs: 2, md: '32px' }, py: '12px', fontSize: 14, fontFamily: J.font, bgcolor: BANNER[tone].bg, color: BANNER[tone].color, borderBottom: `1px solid ${BANNER[tone].border}`, ...(rounded ? { border: `1px solid ${BANNER[tone].border}`, borderRadius: '12px' } : {}) }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 20px' }}>
      <Box component="b" sx={{ fontWeight: 700 }}>{title}</Box>
      {details.map((d, i) => <Box component="span" key={i} sx={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>{d}</Box>)}
    </Box>
    {action}
  </Box>
);

// --- Aviso dentro de un formulario o de una página ---

const NOTICE_ICON: Record<JourneyTone, React.ReactNode> = {
  info: <InfoOutlinedIcon fontSize="small" />,
  warn: <WarningAmberIcon fontSize="small" />,
  danger: <ErrorOutlineIcon fontSize="small" />,
  ok: <CheckCircleOutlineIcon fontSize="small" />,
};

export const Notice: React.FC<{ tone: JourneyTone; children: React.ReactNode; action?: React.ReactNode }> = ({ tone, children, action }) => (
  <Box role={tone === 'danger' ? 'alert' : 'status'} sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start', p: '14px 16px', borderRadius: '12px', fontSize: 14, lineHeight: 1.55, fontFamily: J.font, border: '1px solid', bgcolor: TONE[tone].bg, borderColor: TONE[tone].border, color: TONE[tone].text, '& a': { color: J.skyText, fontWeight: 600, textDecoration: 'none' } }}>
    <Box component="span" sx={{ flex: '0 0 20px', mt: '1px', color: TONE[tone].accent, display: 'flex' }}>{NOTICE_ICON[tone]}</Box>
    <Box component="span" sx={{ flex: 1 }}>{children}</Box>
    {action}
  </Box>
);

// --- Tarjeta de plan, destacada y normal ---

export const PlanCard: React.FC<{
  name: string;
  price: string;
  period: string;
  description?: React.ReactNode;
  features: React.ReactNode[];
  featured?: boolean;
  badge?: React.ReactNode;
  action?: React.ReactNode;
  footnote?: React.ReactNode;
}> = ({ name, price, period, description, features, featured, badge, action, footnote }) => (
  <Box sx={{ borderRadius: J.radiusCard, border: `1px solid ${featured ? J.sky : J.borderStrong}`, bgcolor: 'rgba(30,41,59,.4)', p: '28px', display: 'flex', flexDirection: 'column', gap: '20px', fontFamily: J.font, ...(featured ? { boxShadow: '0 0 0 1px rgba(56,189,248,.35),0 24px 60px rgba(2,6,23,.6)' } : {}) }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <JourneyText variant="h2">{name}</JourneyText>
      {badge && <JourneyChip tone="sky">{badge}</JourneyChip>}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
      <Box component="span" sx={{ fontSize: 40, fontWeight: 800, letterSpacing: '-.03em', color: J.text }}>{price}</Box>
      <Box component="span" sx={{ fontSize: 14, color: J.muted }}>{period}</Box>
    </Box>
    {description && <JourneyText variant="body">{description}</JourneyText>}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: 14, color: J.body }}>
      {features.map((f, i) => (
        <Box key={i} sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <CheckIcon sx={{ flex: '0 0 18px', fontSize: 18, mt: '2px', color: J.skyText }} />
          <span>{f}</span>
        </Box>
      ))}
    </Box>
    {action}
    {footnote && <JourneyText variant="small">{footnote}</JourneyText>}
  </Box>
);

// --- Lista de pasos: hecho, actual y pendiente ---

export type StepState = 'done' | 'current' | 'pending';

export interface JourneyStep { title: React.ReactNode; description?: React.ReactNode; state: StepState; action?: React.ReactNode }

export const StepList: React.FC<{ steps: JourneyStep[] }> = ({ steps }) => (
  <Box component="ol" sx={{ display: 'flex', flexDirection: 'column', gap: '10px', m: 0, p: 0, listStyle: 'none', fontFamily: J.font }}>
    {steps.map((s, i) => (
      <Box component="li" key={i} aria-current={s.state === 'current' ? 'step' : undefined} sx={{ display: 'flex', alignItems: 'center', gap: '16px', p: '16px 18px', borderRadius: '12px', border: `1px solid ${s.state === 'current' ? 'rgba(56,189,248,.45)' : J.border}`, bgcolor: s.state === 'current' ? 'rgba(56,189,248,.06)' : 'rgba(15,23,42,.6)' }}>
        <Box component="span" sx={{ flex: '0 0 32px', height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, border: '1px solid',
          ...(s.state === 'done' ? { bgcolor: J.ok, borderColor: J.ok, color: '#052e16' } : s.state === 'current' ? { bgcolor: J.sky, borderColor: J.sky, color: J.onSky } : { borderColor: J.borderStrong, color: J.body }) }}>
          {s.state === 'done' ? <CheckIcon sx={{ fontSize: 18 }} /> : i + 1}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: 15, fontWeight: 700, color: s.state === 'done' ? J.muted : J.text, textDecoration: s.state === 'done' ? 'line-through' : 'none' }}>{s.title}</Box>
          {s.description && <Box sx={{ fontSize: 13, color: J.muted, mt: '2px', lineHeight: 1.5 }}>{s.description}</Box>}
        </Box>
        {s.action}
      </Box>
    ))}
  </Box>
);

// --- Campo de formulario con error bajo el campo ---

export const JourneyField: React.FC<Omit<InputBaseProps, 'error'> & { label: string; id: string; error?: React.ReactNode; help?: React.ReactNode }> = ({ label, id, error, help, sx, inputProps, ...input }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', fontFamily: J.font }}>
    <Box component="label" htmlFor={id} sx={{ fontSize: 13, fontWeight: 600, color: J.body }}>{label}</Box>
    <InputBase
      id={id}
      error={!!error}
      inputProps={{ 'aria-invalid': !!error, 'aria-describedby': error || help ? `${id}-help` : undefined, ...inputProps }}
      {...input}
      sx={{
        height: 46, borderRadius: J.radiusControl, bgcolor: J.inputBg, border: `1px solid ${error ? J.danger : J.inputBorder}`, color: J.text, px: '14px', fontFamily: J.font, fontSize: 15,
        '&.Mui-focused': { borderColor: error ? J.danger : J.sky, boxShadow: error ? 'none' : '0 0 0 3px rgba(56,189,248,.12)' },
        // PRC-036: el autorrelleno del navegador no pinta el campo de blanco.
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': { WebkitBoxShadow: '0 0 0 1000px ' + J.inputBg + ' inset', WebkitTextFillColor: J.text, caretColor: J.text, transition: 'background-color 9999s ease-out 0s' },
        ...(sx as object),
      }}
    />
    {(error || help) && (
      <Box id={`${id}-help`} sx={{ fontSize: 12, lineHeight: 1.5, color: error ? '#fca5a5' : J.muted, '& a': { color: J.skyText, fontWeight: 600, textDecoration: 'none' } }}>{error || help}</Box>
    )}
  </Box>
);
